<template>
  <ResponsiveLayout
    :progress="progress"
    sticky-actions
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__insurance_search__title_web') }}
        </p>
      </div>

      <!-- The search sticks while this block, list included, scrolls under it,
           and sits 20px above the first row. -->
      <div class="insurance-selection-redesign__picker">
        <form
          class="insurance-selection-redesign__search"
          @submit.prevent
        >
          <TextField
            v-model="query"
            data-qa="insurance-selection-search"
            :label="$t('eu__insurance_search__field_label')"
            :aria-label="$t('eu__insurance_search__field_label')"
            :disabled="submitAttrs.loading"
            autocomplete="off"
            @input="clearSelection"
          >
            <template #prepend>
              <SearchIcon />
            </template>
          </TextField>
        </form>

        <div
          v-if="loadingInsurances || (isSearching && searchLoading)"
          class="insurance-selection__loading"
          data-qa="insurance-selection-loading"
        >
          <KDSSpinner />
        </div>

        <template v-else>
          <fieldset
            class="insurance-selection__fieldset insurance-selection-redesign__fieldset"
          >
            <div
              v-for="{ insurance, logo } in visibleRows"
              :key="insurance.key"
              data-qa="insurance-radio-item"
              :data-key="insurance.key"
              :class="[
                'quiz-radio',
                { 'quiz-radio--checked': isSelected(insurance) },
              ]"
              @click="selectInsurance(insurance)"
            >
              <label
                :for="optionId(insurance)"
                class="quiz-radio__label insurance-selection-redesign__label kds-typography-title-medium"
              >
                <InsuranceOptionLabel
                  :title="insurance.title"
                  :logo="logo"
                  :selected="isSelected(insurance)"
                />
              </label>
              <div class="quiz-radio__layout">
                <CustomRadio
                  :name="optionId(insurance)"
                  :checked="isSelected(insurance)"
                />
              </div>
            </div>
          </fieldset>

          <div
            v-if="showNoResults"
            data-qa="insurance-selection-no-results"
          >
            <p class="kds-typography-title-medium">
              {{ $t('eu__insurance_search__not_found_title') }}
            </p>
            <p class="kds-typography-paragraph-medium">
              {{ $t('eu__insurance_search__not_found_description') }}
            </p>
          </div>

          <p
            v-if="isSearching && searchFailed"
            data-qa="insurance-selection-search-error"
            class="kds-typography-paragraph-medium insurance-selection-redesign__error"
          >
            {{
              $t('eu__insurance_search__search_error_description', {
                email: env.VITE_SUPPORT_EMAIL,
              })
            }}
          </p>
        </template>
      </div>
    </div>

    <template #actions>
      <!-- Submit feedback sits beside the pinned Continue button: below a long
           list it would be off screen and Continue would seem to do nothing. -->
      <p
        v-if="fieldAttrs.insurance.errors?.length"
        data-qa="insurance-selection-error"
        role="alert"
        class="kds-typography-paragraph-medium insurance-selection-redesign__error"
      >
        {{ $t(fieldAttrs.insurance.errors[0]) }}
      </p>

      <BaseBanner
        v-if="showDigaNotSupported"
        variant="warning"
        data-qa="insurance-selection-diga-not-supported"
      >
        <p>{{ $t('eu__corporate_search__diga_not_supported__description') }}</p>
      </BaseBanner>

      <BaseBanner
        v-else-if="showGenericError"
        variant="warning"
        :title="$t('generic__error_title')"
      >
        <p
          v-html="
            $t('generic__error_message', { email: env.VITE_SUPPORT_EMAIL })
          "
        />
      </BaseBanner>
      <BaseButton
        v-if="showDigaNotSupported"
        data-qa="insurance-selection-diga-support"
        :text="$t('eu__corporate_search__submit_button__diga_not_supported')"
        @click="redirectToSupportPage"
      />
      <BaseButton
        v-else
        type="submit"
        :loading="submitAttrs.loading"
        data-qa="insurance-selection-submit"
        :text="$t('eu__corporate_search__primary_cta')"
        @click="handleSubmit"
      />
    </template>
  </ResponsiveLayout>
  <PrescriptionSupportModal
    ref="supportModalRef"
    show-skip
    @skip="navigateNext('supportSkipped')"
  />
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import * as apiRequest from '@shared/api-endpoints'
import env from '@shared/env.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useNavigation from '@eu/app/composables/useNavigation'
import useInsuranceSubmission from '@eu/app/composables/useInsuranceSubmission'
import useCorporateSearch, {
  MIN_QUERY_LENGTH,
} from '@eu/app/composables/useCorporateSearch'
import ROUTE from '@eu/app/router/appModule/names'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import { insuranceLogo } from '@eu/app/helpers/insuranceLogos'
import { trackInsuranceListLoaded } from '@eu/app/helpers/trackInsuranceListLoaded'
import { preloadImage } from '@shared/utils/preloadImage'
import publicInsuranceImageDe from '@shared/assets/img/insurance_covered_public_de.png'
import publicInsuranceImageEn from '@shared/assets/img/insurance_covered_public_en.png'
import privateInsuranceImage from '@shared/assets/img/insurance_covered_private.svg'

import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import TextField from '@shared/components/form/TextField.vue'
import SearchIcon from '@shared/assets/icon/SearchIcon.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import CustomRadio from '@shared/components/CustomRadio.vue'
import InsuranceOptionLabel from '@eu/app/components/InsuranceOptionLabel.vue'
import KDSSpinner from '@shared/components/KDSSpinner.vue'
import PrescriptionSupportModal from '@eu/app/components/PrescriptionSupportModal.vue'

const props = defineProps({
  // When the route started rendering; the load-time event counts from here.
  enteredAt: {
    type: Number,
    default: () => performance.now(),
  },
})

const popularCorporatesPromise = apiRequest.getPopularCorporates()

const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.INSURANCE_SELECTION,
)
const { progress } = useOnboardingProgress()
const corporateStore = useCorporateStore()
const userStore = useUserStore()

const popularInsurances = ref([])
const selectedInsurance = ref(null)
const query = ref('')
const loadingInsurances = ref(true)
const supportModalRef = ref(null)

const {
  fieldAttrs,
  submitAttrs,
  submit,
  showGenericError,
  showDigaNotSupported,
} = useInsuranceSubmission({
  selectedInsurance,
  fieldName: 'insurance',
  navigateNext,
})

const {
  results: searchResults,
  loading: searchLoading,
  failed: searchFailed,
  search,
} = useCorporateSearch(async (searchQuery, abortSignal) => {
  const result = await apiRequest.searchCorporate(searchQuery, '', abortSignal)
  return result?.data?.corporates ?? []
})

watch(query, search)

const isSearching = computed(
  () => query.value.trim().length >= MIN_QUERY_LENGTH,
)
const visibleRows = computed(() =>
  (isSearching.value ? searchResults.value : popularInsurances.value).map(
    (insurance) => ({ insurance, logo: insuranceLogo(insurance) }),
  ),
)
const showNoResults = computed(
  () =>
    isSearching.value &&
    !searchLoading.value &&
    !searchFailed.value &&
    searchResults.value.length === 0,
)

const isSelected = (insurance) => selectedInsurance.value?.key === insurance.key

// The API lists public insurers by popularity, with those that had no recent
// sign-ups A–Z at the end. Private insurers go after them A–Z, including
// those also flagged public.
const byTitle = new Intl.Collator('de').compare
const inListOrder = (insurances) => [
  ...insurances.filter((i) => !i.is_private_insurance),
  ...insurances
    .filter((i) => i.is_private_insurance)
    // sort() on filter()'s copy: toSorted() needs Safari 16 and isn't polyfilled.
    .sort((a, b) => byTitle(a.title, b.title)),
]

// CustomRadio uses its name as the input id, which the row's label points at,
// so screen readers announce each radio with the insurer's name.
const optionId = (insurance) => `insurance-option-${insurance.key}`

onMounted(async () => {
  try {
    const result = await popularCorporatesPromise
    popularInsurances.value = inListOrder(result?.data?.corporates ?? [])
  } catch (error) {
    console.error(error)
    showGenericError.value = true
    popularInsurances.value = []
  } finally {
    const saved = corporateStore.corporate
    // The search is usable while the list loads; what the user typed wins.
    if (saved && !query.value && !selectedInsurance.value) {
      const popular = popularInsurances.value.find((i) => i.key === saved.key)
      selectedInsurance.value = popular ?? saved
      if (!popular) query.value = saved.title
    }

    loadingInsurances.value = false
  }
})

// Reported once, when the list on screen has loaded and rendered: after the
// popular list and any search still running by then, e.g. for a restored
// choice or a query typed while the list loaded.
const listShown = computed(
  () => !loadingInsurances.value && !(isSearching.value && searchLoading.value),
)
const stopReportingLoad = watch(listShown, async (shown) => {
  if (!shown) return
  stopReportingLoad()
  if (showGenericError.value) return
  await nextTick()
  trackInsuranceListLoaded(props.enteredAt, visibleRows.value.length)
})

watch(
  () => corporateStore.corporate,
  (corporate) => {
    if (!corporate) return
    if (corporate.insuranceType !== 'private') {
      preloadImage(
        userStore.lang.startsWith('de')
          ? publicInsuranceImageDe
          : publicInsuranceImageEn,
      )
    } else {
      preloadImage(privateInsuranceImage)
    }
  },
  { immediate: true },
)

// Typing looks for a different insurer, so an earlier pick may no longer be on
// screen. Restoring a saved choice sets the query without an input event and
// keeps its selection.
const clearSelection = () => {
  selectedInsurance.value = null
}

const selectInsurance = (insurance) => {
  selectedInsurance.value = insurance

  // Clear any previous submit error once the user changes the selection.
  if (fieldAttrs.value.insurance.errors?.length) {
    fieldAttrs.value.insurance.onInput()
  }
}

const handleSubmit = () => submit()

const redirectToSupportPage = () => {
  supportModalRef.value.open()
}
</script>

<style lang="scss">
@use '@eu/app/views/css/insuranceSelectionList.scss';

.insurance-selection-redesign__fieldset {
  // The designs space the rows 12px apart; the current screen keeps 8px.
  gap: $kds-space-1_5x;
}

.insurance-selection-redesign__search {
  position: sticky;
  // Right below the layout header and its 4px progress bar; the header is 46px
  // on mobile and 70px from md up.
  top: calc(50px + env(safe-area-inset-top));
  z-index: 1;
  // The bottom padding is the designs' 20px gap to the first row.
  padding: $kds-space-1x 0 $kds-space-2_5x;
  background-color: $kds-color-background-base;

  @include media-up(md) {
    top: calc(74px + env(safe-area-inset-top));
  }
}

.insurance-selection-redesign__error {
  color: $kds-color-text-interactive-destructive;
}

.insurance-selection-redesign__label {
  display: flex;
  align-items: center;
  gap: $kds-space-1x;
}
</style>
